import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Params } from "@angular/router";
import {
  EnquiryService,
  NotificationService,
  ProfileService,
  DocumentStatus,
  NotificationKeys,
  ResponseStatus,
  UserTypes,
  fileTypes,
  fileUploadStatus,
  externalImageLinks,
} from "@core";
import { TranslateService } from "@ngx-translate/core";
import { NzUploadFile } from "ng-zorro-antd/upload";
import { Observable, Observer } from "rxjs";
import { pageRoutes } from "src/app/core/config/page-routes.config";

@Component({
  selector: "app-ticket-information",
  templateUrl: "./ticket-information.component.html",
  styleUrls: ["./ticket-information.component.less"],
  standalone: false,
})
export class TicketInformationComponent implements OnInit {
  @ViewChild("ScrollToBottom", { read: ElementRef }) scrollbottom!: ElementRef;
  routeId!: string;
  userId: any;
  commentForm!: FormGroup;
  ticketComments: any[] = [];
  ticketDetails: any[] = [];
  uploadDocuments: boolean = false;
  date = new Date();
  ticketStatus: string = "ENQUIRY.TICKET-INFORMATION.OPEN";
  selectedDocument: any;
  notificationKeys = NotificationKeys;

  uploadForm!: FormGroup;
  uploadDocumentsModal: boolean = false;
  uploadedDocumentsModal: boolean = false;
  docToUpload: any;
  documentFiles: any;
  uploading: boolean = false;
  pageRoutes = pageRoutes;

  userTypes = UserTypes;

  // PAGINATION
  page = 1;

  constructor(
    private route: ActivatedRoute,
    private enquiryService: EnquiryService,
    private profileService: ProfileService,
    private notify: NotificationService,
    private translate: TranslateService,
    fb: FormBuilder,
  ) {
    this.userId = this.profileService.getLocalStorageUser()?.id;
    this.commentForm = fb.group({
      message: ["", Validators.required],
      ticket_id: [""],
      send_mail: false,
    });
    this.uploadForm = fb.group({
      documentFiles: fb.array([], Validators.required),
    });
  }

  ngOnInit(): void {
    this.getRouteId();
    this.getTicketComments();
    this.scrollToBottomOnLoadPage();
  }

  setDocument(doc: any) {
    this.docToUpload = doc;
  }

  uploadedDocCount: number = 0;
  successfulUploadCount: number = 0;

  preDocumentsUpload = (file: NzUploadFile): Observable<boolean> =>
    new Observable((observer: Observer<boolean>) => {
      const isJpgOrPng =
        file.type === fileTypes.JPG ||
        file.type === fileTypes.PNG ||
        file.type === fileTypes.PDF;
      if (!isJpgOrPng) {
        this.notify.error(
          "",
          this.translate.instant("ENQUIRY.TICKET-INFORMATION.ONLY_PNG_JPG"),
          NotificationKeys.CERTIFICATE,
        );
        observer.complete();
        this.uploading = false;
        return;
      }
      const isLt2M = file.size! / (3 * 1024 * 1024) < 1;
      if (!isLt2M) {
        this.notify.error(
          "",
          this.translate.instant("ENQUIRY.TICKET-INFORMATION.DOCUMENT_SIZE"),
          this.notificationKeys.CERTIFICATE,
        );
        observer.complete();
        this.uploading = false;
        return;
      }

      this.documentsRequired[this.getIndex(this.docToUpload)].fileList.push(
        file,
      );
      this.uploadedDocCount++;

      observer.complete();
    });

  getIndex(val: any) {
    const index = this.documentsRequired.findIndex(
      (obj: any) => obj.formControlName === val,
    );
    return index;
  }

  documentsRequired: any[] = [];
  documentsList: NzUploadFile[] = [];

  handleModalCancel() {
    // this.documentsRequired = [];
    this.resetFileLists();
    this.uploadDocumentsModal = false;
    this.uploadedDocumentsModal = false;
  }

  resetFileLists() {
    for (let file of this.documentsRequired) {
      file.fileList = [];
      file.uploadStatus = fileUploadStatus.PENDING;
    }
  }

  removeItem(file: NzUploadFile, controlIndex: number) {
    const control = this.documentsRequired[controlIndex];
    const index = control.fileList.findIndex(
      (item: any) => item.uid === file.uid,
    );
    if (index !== -1) {
      control.fileList.splice(index, 1);
      this.uploadedDocCount--;
    }
  }

  disableSubmit: boolean = false;

  async submitDocuments() {
    this.disableSubmit = false;
    for (let entry of this.documentsRequired) {
      if (!entry.fileList.length) {
        this.notify.error(
          "",
          `${this.translate.instant("ENQUIRY.KINDLY_UPLOAD")} ${
            entry.document_type?.name
          }`,
        );
        this.disableSubmit = true;
        return;
      }

      this.uploadStatus = fileUploadStatus.UPLOADING;
      entry.uploadStatus = this.uploadStatus;

      // await this.simulateApiCall(entry);
      await this.uploadDocument(entry);
      entry.uploadStatus = this.uploadStatus;
    }

    if (this.successfulUploadCount == this.documentsRequired.length) {
      setTimeout(() => {
        this.uploadDocumentsModal = false;
        this.documentsRequired = [];
        this.getRouteId();
        this.page = 1;
        this.getTicketComments();
      }, 2000);
    }
    this.uploadDocuments = false;
    this.uploadStatus = "";
    this.uploadedDocCount = 0;
  }

  uploadStatus: any = "";

  async uploadDocument(obj: any) {
    this.uploading = true;
    const formData = new FormData();
    formData.append("document_id", obj?.id);
    formData.append("file", obj?.fileList[0]);

    await this.enquiryService
      .uploadDocument(formData)
      .toPromise()
      .then(() => {
        this.uploading = false;
        this.successfulUploadCount++;
        this.notify.success(
          "",
          `${obj.document_type.name} ${this.translate.instant(
            "ENQUIRY.UPLOADED_SUCCESSFULLY",
          )}`,
        );
        this.uploadStatus = fileUploadStatus.UPLOADED;
      })
      .catch((err: any) => {
        this.uploading = false;
        this.notify.error("", err?.error?.message);
        console.warn(err);
        this.uploadStatus = fileUploadStatus.FAILED;
      });
  }

  openUploadModal() {
    this.uploadDocumentsModal = true;
  }

  getUserType(data: any) {
    let user = data?.firstname + " " + data?.surname;
    if (data?.source === UserTypes.ADMIN) {
      user = data?.created_by?.firstname;
      // return user;
    }
    let instutition = data?.name;

    if (instutition !== null) {
      return instutition;
    }

    return user;
  }

  getAdminName(data: any) {
    let name = "";
    if (data?.source === UserTypes.ADMIN) {
      name = data?.created_by?.firstname;
    }
    return name;
  }

  scrollToBottomOnLoadPage() {
    setTimeout(() => {
      this.scrollbottom.nativeElement.scrollTop =
        this.scrollbottom.nativeElement.scrollHeight;
    }, 3000);
  }

  getRouteId() {
    this.route.params.subscribe((param: Params) => {
      this.routeId = param.id;
    });
    this.getSingleTicket(this.routeId);
  }

  submittedDocCount: number = 0;
  doc: any;
  selectedDocImage: any = externalImageLinks.SELECTED_DOC;

  showDoc() {
    // this.fetchImageData();
    this.selectedDocImage = this.doc?.url;
  }

  getSingleTicket(id: any) {
    this.uploadDocuments = false;
    this.enquiryService
      .getSingleTicket(id)
      .then((res: any) => {
        if (res?.status === ResponseStatus.OK) {
          this.ticketDetails = [];
          this.ticketDetails.push(res?.data);
          if (this.ticketDetails[0].documents.length) {
            this.getDocumentCount(this.ticketDetails[0].documents);
            for (let document of this.ticketDetails[0].documents) {
              if (
                document.status === DocumentStatus.PENDING ||
                document?.status === DocumentStatus.REJECTED
              ) {
                let file = {
                  formControlName: document?.document_type?.code,
                  fileList: [],
                  errorState: false,
                  uploadStatus: fileUploadStatus.PENDING,
                  ...document,
                };
                this.uploadDocuments = true;
                this.documentsRequired.push(file);
              }
            }
          }
          this.ticketStatus = res?.data?.status;
        }
      })
      .catch((err: any) => {
        console.error(err);
      });
  }

  getTicketComments() {
    this.enquiryService
      .listTicketComments(this.routeId, this.page)
      .then((res: any) => {
        if (res?.status === ResponseStatus.OK) {
          this.ticketComments.push();
          this.ticketComments = res?.data?.data.reverse();
        }
      })
      .catch((err: any) => {
        console.error(err?.error?.message);
      });
  }

  getDocumentCount(data: any) {
    this.submittedDocCount = 0;
    for (let doc of data) {
      if (
        doc?.status === DocumentStatus.SUBMITTED ||
        doc?.status === DocumentStatus.APPROVED
      ) {
        this.submittedDocCount++;
      }
    }
  }

  openUploadedDocumentsModal() {
    this.doc = this.ticketDetails[0]?.documents[0];
    console.log(this.doc);
    this.uploadedDocumentsModal = true;
  }

  closeModal() {
    this.uploadedDocumentsModal = false;
  }

  onSendComment() {
    this.commentForm.patchValue({ ticket_id: this.routeId });
    this.enquiryService
      .makeComment(this.commentForm.value)
      .then((res: any) => {
        if (res?.status === ResponseStatus.FAILED) {
          this.notify.info("", res?.message);
          return;
        }

        this.notify.success("", res?.message);
        this.page = 1;
        this.getTicketComments();
        this.commentForm.reset();
      })
      .catch((err: any) => {
        console.error(err?.error?.message);
      });
  }

  onScrollDown() {
    this.enquiryService
      .listTicketComments(this.routeId, ++this.page)
      .then((res: any) => {
        if (res?.status === ResponseStatus.OK) {
          let data = res?.data?.data.reverse();
          if (data.length) {
            this.ticketComments.unshift(...data);
          }
        }
      })
      .catch((err: any) => {
        console.error(err?.error?.message);
      });
  }

  onScrollUp() {
    this.onScrollDown();
  }
}
